import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Check, Languages as LanguagesIcon } from 'lucide-react';
import type { LanguageToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { SystemGrid, type SystemGridColumn } from '~/components/system/grid/system-grid';
import { serverMessage } from '~/lib/api/server-error';
import { useLanguagesList, useLanguageMutations } from '~/hooks/use-languages';
import { tokenToForm, type LanguageFormValues } from '~/lib/schemas/language';
import { LanguageEditDialog } from './language-edit-dialog';

/** Languages CRUD surface — parity with the legacy admin grid (Name/LocalName/ISO-2/Culture/Visible/Sort). */
export function LanguagesGridView() {
	const { t } = useTranslation();
	const { data, isLoading, isError, refetch } = useLanguagesList();
	const { remove } = useLanguageMutations();

	const [editOpen, setEditOpen] = useState(false);
	const [editing, setEditing] = useState<LanguageFormValues | null>(null);
	const [deleteRow, setDeleteRow] = useState<LanguageToken | null>(null);

	const columns = useMemo<SystemGridColumn<LanguageToken>[]>(
		() => [
			{
				key: 'name',
				header: t('system.languages.columns.name'),
				width: 'minmax(0,1fr)',
				sortValue: l => l.nm,
				exportValue: l => l.nm,
				render: l => <span className="truncate text-sm font-medium text-foreground">{l.nm}</span>
			},
			{
				key: 'localName',
				header: t('system.languages.columns.localName'),
				width: 'minmax(0,1fr)',
				sortValue: l => l.ln,
				exportValue: l => l.ln,
				render: l => <span className="truncate text-sm text-foreground-muted">{l.ln}</span>
			},
			{
				key: 'iso2',
				header: t('system.languages.columns.iso2'),
				width: '90px',
				sortValue: l => l.iso2,
				exportValue: l => l.iso2,
				render: l => <span className="font-mono text-xs text-foreground-muted">{l.iso2}</span>
			},
			{
				key: 'culture',
				header: t('system.languages.columns.culture'),
				width: '110px',
				sortValue: l => l.cc,
				exportValue: l => l.cc,
				render: l => <span className="font-mono text-xs text-foreground-muted">{l.cc}</span>
			},
			{
				key: 'visible',
				header: t('system.languages.columns.visible'),
				width: '80px',
				align: 'center',
				sortValue: l => (l.iv ? 1 : 0),
				exportValue: l => (l.iv ? t('common.yes') : t('common.no')),
				render: l => (l.iv ? <Check size={16} className="text-success-fg" /> : <span className="text-foreground-disabled">—</span>)
			},
			{
				key: 'sort',
				header: t('system.languages.columns.sort'),
				width: '70px',
				align: 'right',
				sortValue: l => l.si ?? 0,
				exportValue: l => l.si ?? 0,
				render: l => <span className="text-sm tabular-nums text-foreground-muted">{l.si}</span>
			}
		],
		[t]
	);

	const confirmDelete = () => {
		if (!deleteRow?.id) return;
		remove.mutate(deleteRow.id, {
			onSuccess: () => {
				toast.success(t('common.delete'));
				setDeleteRow(null);
			},
			onError: err => toast.error(serverMessage(err))
		});
	};

	return (
		<>
			<SystemGrid<LanguageToken>
				gridId="languages"
				title={t('system.languages.title')}
				rows={data ?? []}
				loading={isLoading}
				error={isError}
				onRetry={() => void refetch()}
				getRowId={l => l.id ?? l.iso2}
				columns={columns}
				searchPredicate={(l, q) => `${l.nm ?? ''} ${l.ln ?? ''} ${l.iso2 ?? ''} ${l.cc ?? ''}`.toLowerCase().includes(q)}
				defaultSort={{ key: 'sort', dir: 'asc' }}
				emptyIcon={<LanguagesIcon size={36} className="text-foreground-disabled" strokeWidth={1.25} />}
				addLabel={t('system.languages.add')}
				onAdd={() => {
					setEditing(null);
					setEditOpen(true);
				}}
				onEdit={l => {
					setEditing(tokenToForm(l));
					setEditOpen(true);
				}}
				onDelete={setDeleteRow}
				exportFilename="languages"
			/>

			<LanguageEditDialog open={editOpen} onOpenChange={setEditOpen} initial={editing} />

			<Dialog open={Boolean(deleteRow)} onOpenChange={open => !open && setDeleteRow(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t('system.languages.deleteTitle')}</DialogTitle>
						<DialogDescription>{t('system.languages.deleteBody', { name: deleteRow?.nm ?? '' })}</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="ghost" onClick={() => setDeleteRow(null)}>
							{t('common.cancel')}
						</Button>
						<Button variant="destructive" onClick={confirmDelete} disabled={remove.isPending}>
							{t('common.delete')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
